import {
  carePlanComposeHref,
  carePlanDetailHref,
  clientJourneyHref,
  type ClientCarePlanReturnContext,
} from '@/lib/sah/client-care-plan-navigation';

export type SahCarePlanDestinationIntent =
  | 'continue'
  | 'author'
  | 'budget'
  | 'services'
  | 'goals'
  | 'risks'
  | 'preferences'
  | 'interventions'
  | 'worker_instructions'
  | 'agreements'
  | 'reviews'
  | 'delivery'
  | 'record'
  | 'audit'
  | 'export'
  | 'amend';

export type SahCarePlanDestinationSurface =
  | 'client_care_plan_workspace'
  | 'client_journey'
  | 'composer'
  | 'record'
  | 'printable';

export type SahCarePlanDestinationPlan = {
  id: string;
  status?: string | null;
};

export type SahCarePlanDestinationDecision = {
  href: string;
  surface: SahCarePlanDestinationSurface;
  reason: string;
};

type SahCarePlanDestinationInput = {
  clientId: string;
  plan?: SahCarePlanDestinationPlan | null;
  intent?: SahCarePlanDestinationIntent;
  returnContext?: ClientCarePlanReturnContext;
  hasCurrentServiceInclusions?: boolean;
  composeDefaultRedirect?: boolean;
};

const COMPOSER_STATUSES = new Set(['DRAFT', 'IN_REVIEW', 'PENDING_APPROVAL', 'IN_PROGRESS']);
const RECORD_STATUSES = new Set([
  'ACTIVE',
  'COMPLETED',
  'CEASED',
  'CANCELLED',
  'TRANSFERRED',
  'SUPERSEDED',
]);

const COMPOSER_ANCHORS: Partial<Record<SahCarePlanDestinationIntent, string>> = {
  author: 'composer-assess',
  budget: 'budget',
  services: 'services',
  goals: 'goals',
  risks: 'risks',
  preferences: 'preferences',
  interventions: 'interventions',
  worker_instructions: 'worker_instructions',
  agreements: 'composer-agree',
  reviews: 'reviews',
  delivery: 'composer-activate',
};

const RECORD_ANCHORS: Partial<Record<SahCarePlanDestinationIntent, string>> = {
  budget: 'budget',
  services: 'services',
  goals: 'goals',
  risks: 'risks',
  preferences: 'preferences',
  interventions: 'interventions',
  worker_instructions: 'worker_instructions',
  agreements: 'signing',
  reviews: 'review-requests',
  delivery: 'delivery-readiness',
  audit: 'audit',
};

function normalizeStatus(status: unknown): string {
  return String(status ?? '')
    .trim()
    .toUpperCase();
}

function composerAnchor(intent: SahCarePlanDestinationIntent): string | undefined {
  return COMPOSER_ANCHORS[intent];
}

function recordAnchor(
  intent: SahCarePlanDestinationIntent,
  hasCurrentServiceInclusions: boolean | undefined,
): string | undefined {
  if (intent === 'continue' || intent === 'record') {
    return hasCurrentServiceInclusions === false ? 'services' : undefined;
  }
  return RECORD_ANCHORS[intent];
}

export function isSahCarePlanComposerStatus(status: unknown): boolean {
  return COMPOSER_STATUSES.has(normalizeStatus(status));
}

export function isSahCarePlanRecordStatus(status: unknown): boolean {
  return RECORD_STATUSES.has(normalizeStatus(status));
}

/**
 * Register / header "Open" door: drafts and in-flight plans author in compose,
 * active and closed plans open as the record. Always emit the live path — never
 * a retired client-scoped alias.
 */
export function carePlanOpenHref(
  carePlanId: string,
  options?: {
    status?: string | null;
    hash?: string;
    returnContext?: ClientCarePlanReturnContext;
  },
): string {
  if (isSahCarePlanComposerStatus(options?.status)) {
    return carePlanComposeHref(carePlanId, {
      hash: options?.hash,
      returnContext: options?.returnContext,
    });
  }
  return carePlanDetailHref(carePlanId, {
    hash: options?.hash,
    returnContext: options?.returnContext,
  });
}

export function resolveSahCarePlanDestination(
  input: SahCarePlanDestinationInput,
): SahCarePlanDestinationDecision {
  const intent = input.intent ?? 'continue';
  const returnContext =
    input.returnContext ?? ({ clientId: input.clientId, returnTab: 'sah-hub' } as const);

  if (!input.plan?.id) {
    return {
      href: clientJourneyHref(input.clientId, {
        tab: 'care-plans',
        hash: 'care-plan-start',
      }),
      surface: 'client_care_plan_workspace',
      reason:
        'No current SAH support plan exists; open the start panel to mint a draft — not the book-wide list. The panel mounts on Care Plans (Journey timeline-only does not).',
    };
  }

  if (intent === 'export') {
    return {
      href: `/care-plans/${input.plan.id}/printable`,
      surface: 'printable',
      reason: 'Printable/export tasks use the immutable plan artifact.',
    };
  }

  if (intent === 'amend') {
    return {
      href: carePlanComposeHref(input.plan.id, { mode: 'amendment', returnContext }),
      surface: 'composer',
      reason: 'Amendments are explicit and create or continue a governed composer working copy.',
    };
  }

  const status = normalizeStatus(input.plan.status);
  if (COMPOSER_STATUSES.has(status)) {
    return {
      href: carePlanComposeHref(input.plan.id, {
        hash: composerAnchor(intent),
        returnContext,
      }),
      surface: 'composer',
      reason: 'Draft/review SAH support-plan work belongs in the composer.',
    };
  }

  if (RECORD_STATUSES.has(status)) {
    if (status === 'ACTIVE' && (intent === 'preferences' || intent === 'interventions')) {
      return {
        href: carePlanComposeHref(input.plan.id, {
          hash: composerAnchor(intent),
          returnContext,
        }),
        surface: 'composer',
        reason:
          'Preference and intervention writes belong in the composer even when the current plan is active.',
      };
    }
    return {
      href: carePlanDetailHref(input.plan.id, {
        hash: recordAnchor(intent, input.hasCurrentServiceInclusions),
        returnContext,
      }),
      surface: 'record',
      reason: 'Active and closed SAH support plans open as records unless amendment is explicit.',
    };
  }

  return {
    href: carePlanComposeHref(input.plan.id, {
      hash: composerAnchor(intent),
      returnContext,
    }),
    surface: 'composer',
    reason: 'Unknown in-flight support-plan status defaults to composer for provider resolution.',
  };
}
